import { useCallback, useState } from "react";
import { debounce } from "@mui/material";
import useKlydosReviewQuery from "../../../../components/hooks/useKlydosReviewQuery";
import { Column } from "../../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { Klydo } from "../../../../api/klydo";

const PAGE_SIZE = 20;

export const REVIEW_UPDATED_AT_FIELD = "public.reviewStatus.updatedAt";

const useKlydoReviewsContainer = () => {
  const [textSearch, setTextSearch] = useState<string>("");
  const [sortField, setSortField] = useState<string>(REVIEW_UPDATED_AT_FIELD);
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const handleFreeSearch = useCallback(
    debounce((s) => setTextSearch(s), 500),
    [],
  );

  const {
    data: klydos,
    fetchNextPage,
    isLoading,
  } = useKlydosReviewQuery(
    textSearch,
    PAGE_SIZE,
    {},
    false,
    sortField,
    sortDirection,
  );

  const handleSortClick = useCallback(
    (field: string) => {
      if (field === "favorites") {
        if (sortField === "stats.favorites") {
          setSortDirection((prev) => (prev === 1 ? -1 : 1));
        } else {
          setSortField("stats.favorites");
        }
      } else {
        if (field === sortField) {
          setSortDirection((prev) => (prev === 1 ? -1 : 1));
        }
        setSortField(field);
      }
    },
    [sortDirection, sortField],
  );

  const columns: Column<Klydo>[] = [
    { field: "name", label: "Name" },
    { field: "loopUrl", label: "LoopUrl" },
    { field: "author", label: "Author" },
    // Keyed "public", not "reviewStatus": KlydosReviewsContainer matches on this
    // exact string to render `public.reviewStatus.state`.
    { field: "public", label: "Status" },
    // Rating stats are computed on-demand per row, not klydo fields — the
    // container maps these to the batched rating-stats lookup. Sortable via a
    // server-side $lookup on klydo-ratings (see KlydoSearchProvider), so the
    // sort spans the whole result set, not just the loaded page.
    { field: "avgRating" as keyof Klydo, label: "Avg Rating", sortable: true },
    {
      field: "ratingCount" as keyof Klydo,
      label: "Rating Count",
      sortable: true,
    },
    { field: "createdAt", label: "Created At" },
    {
      field: REVIEW_UPDATED_AT_FIELD as keyof Klydo,
      label: "Last Review Update",
      sortable: true,
    },
  ];

  const sortState = { [sortField]: sortDirection };

  return {
    klydos,
    isLoading,
    fetchNextPage,
    handleFreeSearch,
    handleSortClick,
    sortState,
    columns,
  };
};
export default useKlydoReviewsContainer;
